import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { XStack, YStack } from "tamagui";
import { useNavigate } from "react-router-dom";

import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { useAuth } from "@/contexts/AuthContext";
import {
  AdminInitialAvatar,
  AdminPageScaffold,
  AdminStatusPill,
} from "@/components/AdminScaffold";
import {
  AppButton,
  AppButtonRow,
  AppCard,
  AppCardBody,
  AppEmptyState,
  AppLoadingState,
  AppText,
  kindredFontFamilies,
} from "../../ui-foundation";

type ModerationTab = "content" | "members";
type ModerationReport = Database["public"]["Tables"]["reports"]["Row"] & {
  reporter?: {
    display_name: string | null;
    avatar_url: string | null;
  } | null;
  reported?: {
    display_name: string | null;
    avatar_url: string | null;
  } | null;
};

const AdminModeration = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [reports, setReports] = useState<ModerationReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<ModerationTab>("content");

  const fetchReports = async () => {
    const { data } = await supabase
      .from("reports")
      .select(
        "*, reporter:profiles!reports_reporter_id_fkey(display_name, avatar_url), reported:profiles!reports_reported_user_id_fkey(display_name, avatar_url)",
      )
      .eq("status", "pending")
      .order("created_at", { ascending: false });

    if (data) {
      setReports(data as ModerationReport[]);
    }
    setLoading(false);
  };

  useEffect(() => {
    void fetchReports();
  }, []);

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffHours = (now.getTime() - date.getTime()) / 3600000;
    if (diffHours < 1) return `${Math.max(1, Math.floor(diffHours * 60))}m ago`;
    if (diffHours < 24) return `${Math.floor(diffHours)}h ago`;
    return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  };

  const resolveReport = async (reportId: string, action: string) => {
    if (!user) return;

    await supabase
      .from("reports")
      .update({
        status: "reviewed",
        resolution_action: action,
        resolved_by: user.id,
        resolved_at: new Date().toISOString(),
      })
      .eq("id", reportId);

    const report = reports.find((entry) => entry.id === reportId);
    if (report) {
      await supabase.from("moderation_log").insert({
        moderator_id: user.id,
        target_user_id: report.reported_user_id || report.reporter_id,
        action,
        content_type: report.content_type,
        content_id: report.content_id,
        notes: `Report resolved: ${report.reason}`,
      });
    }

    toast.success(`Report ${action}`);
    void fetchReports();
  };

  const hideContent = async (reportId: string) => {
    const report = reports.find((entry) => entry.id === reportId);
    if (!report?.content_type || !report?.content_id) return;

    const tableMap: Record<string, string> = {
      feed_post: "feed_posts",
      channel_post: "posts",
      chat_message: "chat_room_messages",
      adventure_message: "adventure_messages",
    };

    const table = tableMap[report.content_type];
    if (table === "feed_posts") {
      await supabase.from("feed_posts").update({ is_deleted: true }).eq("id", report.content_id);
    } else if (table === "chat_room_messages") {
      await supabase.from("chat_room_messages").update({ is_deleted: true }).eq("id", report.content_id);
    } else if (table === "posts") {
      await supabase.from("posts").delete().eq("id", report.content_id);
    } else if (table === "adventure_messages") {
      await supabase.from("adventure_messages").delete().eq("id", report.content_id);
    }

    await resolveReport(reportId, "hidden");
  };

  const contentReports = useMemo(() => reports.filter((report) => report.content_type), [reports]);
  const memberReports = useMemo(
    () => reports.filter((report) => report.reported_user_id && !report.content_type),
    [reports],
  );

  const contentTypeLabel = (contentType: string) => {
    const labels: Record<string, string> = {
      feed_post: "Feed post",
      channel_post: "Channel post",
      chat_message: "Chat message",
      dm: "Direct message",
      adventure_message: "Adventure chat",
    };
    return labels[contentType] || contentType;
  };

  const visibleReports = activeTab === "content" ? contentReports : memberReports;

  return (
    <AdminPageScaffold
      title="Moderation queue"
      subtitle="Review flagged content and reported members from the shared admin surface."
      fallbackPath="/admin"
    >
      {loading ? (
        <AppLoadingState
          title="Loading moderation queue"
          body="Fetching flagged content, reported members, and moderation context."
        />
      ) : (
        <>
          <AppButtonRow>
            <AppButton
              type="button"
              tone={activeTab === "content" ? "secondary" : "outline"}
              size="sm"
              onPress={() => setActiveTab("content")}
            >
              Flagged content ({contentReports.length})
            </AppButton>
            <AppButton
              type="button"
              tone={activeTab === "members" ? "secondary" : "outline"}
              size="sm"
              onPress={() => setActiveTab("members")}
            >
              Reported members ({memberReports.length})
            </AppButton>
          </AppButtonRow>

          {visibleReports.length === 0 ? (
            <AppEmptyState
              title={activeTab === "content" ? "No flagged content" : "No reported members"}
              body={
                activeTab === "content"
                  ? "Nothing is waiting for review right now."
                  : "There are no member reports waiting in the queue."
              }
            />
          ) : (
            <YStack gap="$sm">
              {activeTab === "content"
                ? contentReports.map((report) => (
                    <AppCard key={report.id}>
                      <AppCardBody>
                        <XStack alignItems="center" gap="$sm" flexWrap="wrap">
                          <AdminStatusPill tone="secondary">
                            {contentTypeLabel(report.content_type)}
                          </AdminStatusPill>
                          <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm">
                            {formatTime(report.created_at)}
                          </AppText>
                        </XStack>
                        <AppText fontSize="$sm" lineHeight="$md">
                          {report.reason}
                        </AppText>
                        <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm">
                          Reported by {report.reporter?.display_name ?? "Member"}
                        </AppText>
                        <AppButtonRow>
                          <AppButton
                            type="button"
                            tone="outline"
                            size="sm"
                            onPress={() => resolveReport(report.id, "dismissed")}
                          >
                            Dismiss
                          </AppButton>
                          <AppButton
                            type="button"
                            tone="danger"
                            size="sm"
                            onPress={() => void hideContent(report.id)}
                          >
                            Hide
                          </AppButton>
                          <AppButton
                            type="button"
                            tone="outline"
                            size="sm"
                            onPress={() => resolveReport(report.id, "escalated")}
                          >
                            Escalate
                          </AppButton>
                        </AppButtonRow>
                      </AppCardBody>
                    </AppCard>
                  ))
                : memberReports.map((report) => (
                    <AppCard key={report.id}>
                      <AppCardBody>
                        <XStack alignItems="center" gap="$sm">
                          <AdminInitialAvatar name={report.reported?.display_name ?? "Member"} />
                          <YStack gap={4}>
                            <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$sm" lineHeight="$sm">
                              {report.reported?.display_name ?? "Member"}
                            </AppText>
                            <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm">
                              {formatTime(report.created_at)}
                            </AppText>
                          </YStack>
                        </XStack>
                        <AppText fontSize="$sm" lineHeight="$md">
                          {report.reason}
                        </AppText>
                        <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm">
                          Reported by {report.reporter?.display_name ?? "Member"}
                        </AppText>
                        <AppButtonRow>
                          <AppButton
                            type="button"
                            tone="outline"
                            size="sm"
                            onPress={() => resolveReport(report.id, "dismissed")}
                          >
                            Dismiss
                          </AppButton>
                          <AppButton
                            type="button"
                            tone="secondary"
                            size="sm"
                            onPress={() => resolveReport(report.id, "warned")}
                          >
                            Warn
                          </AppButton>
                          <AppButton
                            type="button"
                            tone="outline"
                            size="sm"
                            onPress={() => report.reported_user_id && navigate(`/admin/members/${report.reported_user_id}`)}
                          >
                            View profile
                          </AppButton>
                          <AppButton
                            type="button"
                            tone="outline"
                            size="sm"
                            onPress={() => resolveReport(report.id, "escalated")}
                          >
                            Escalate
                          </AppButton>
                        </AppButtonRow>
                      </AppCardBody>
                    </AppCard>
                  ))}
            </YStack>
          )}
        </>
      )}
    </AdminPageScaffold>
  );
};

export default AdminModeration;
